/**
 * Correction profile per source (development plan §10.3, amendment 9).
 *
 * Corrections never change an original. They are applied when rendering, before crop/scale and captions
 * (audio: per mic, before the mix). Every key is optional: absent, `null` or `false` means off. Turning a
 * correction off and re-rendering always works.
 *
 * Stored in `sources.corrections` (jsonb). Until a person edits it, the effective profile is the
 * worker's `media_info.recommended_corrections` (written at ingest, with the camera-match LUT added at sync).
 */

/** Providers per capability. Tier A: ffmpeg, deepfilternet, lanczos. Tier B (GPU): realesrgan. Tier C (paid): auphonic, elevenlabs, topaz. */
export const CORRECTION_PROVIDERS = ['ffmpeg', 'deepfilternet', 'lanczos', 'realesrgan', 'auphonic', 'elevenlabs', 'topaz'] as const;
export type CorrectionProvider = (typeof CORRECTION_PROVIDERS)[number];

export const AUDIO_ENHANCERS = ['ffmpeg', 'deepfilternet', 'auphonic', 'elevenlabs'] as const;
export type AudioEnhancerProvider = (typeof AUDIO_ENHANCERS)[number];
export const VIDEO_SCALERS = ['lanczos', 'realesrgan', 'topaz'] as const;
export type VideoScaler = (typeof VIDEO_SCALERS)[number];

/** Tier of a provider: A runs inside render on the CPU (free); B needs the GPU worker; C is a paid API. */
export function providerTier(p: CorrectionProvider): 'A' | 'B' | 'C' {
  if (p === 'realesrgan') return 'B';
  if (p === 'auphonic' || p === 'elevenlabs' || p === 'topaz') return 'C';
  return 'A';
}

/** Reinhard statistics transfer in OKLab (L, a, b), from which cf-media builds or rebuilds the `.cube` LUT. */
export interface ColourTransfer {
  space: 'oklab';
  src_mean: [number, number, number];
  src_std: [number, number, number];
  ref_mean: [number, number, number];
  ref_std: [number, number, number];
  /** Cube edge length (default 33). */
  lut_size?: number;
}

export interface Corrections {
  schema?: 'castfeeds.corrections/1';
  /** `auto` = recommended by the worker, `human` = edited in Fix & enhance. */
  origin?: 'auto' | 'human';
  updated_at?: string;

  // ---- video (applied per piece, before fps conform, crop and scale) ----
  /** bwdif, both fields → one frame per frame. */
  deinterlace?: { parity?: 'auto' | 'tff' | 'bff' } | null | false;
  /** Camera match against the reference camera: `.cube` LUT through lut3d. */
  colour_lut?: {
    /** Storage key of the LUT (`ws/…/ep/…/luts/<source_id>.cube`, amendment 9b). */
    uri?: string;
    /** Local path (render's sources.json only). */
    path?: string;
    reference_id?: string;
    /** 0..1 blend between the original and the matched colour (default 1). */
    strength?: number;
    transfer?: ColourTransfer;
    delta_e_before?: number;
    delta_e_after?: number;
  } | null | false;
  /** Automatic white balance for guest phones and webcams (never together with colour_lut). */
  white_balance?: { method?: 'grayworld' | 'colorcorrect' } | null | false;
  /** eq: gamma lift for dark sources. */
  exposure?: { gamma?: number; brightness?: number; contrast?: number; saturation?: number } | null | false;
  /** hqdn3d (Tier A); realesrgan / topaz run in the enhance stage (tiers B/C). */
  denoise_video?: { strength?: number; provider?: 'ffmpeg' | 'realesrgan' | 'topaz' } | null | false;
  /** Two-pass vidstab, handheld sources only. */
  stabilize?: { smoothing?: number; shakiness?: number } | null | false;
  /** Conform a variable-frame-rate source to a constant rate before the other filters. */
  fps?: { fps: number; mode?: 'drop' | 'blend' } | null | false;
  /** Upscaler for this source when the output is larger than the source (the EDL's `format.scaler` is the default). */
  upscale?: { provider: VideoScaler } | null | false;

  // ---- audio (per mic, before the mix) ----
  highpass_hz?: number | null | false;
  /** Mains frequency (50 Hz in Israel and Europe, 60 Hz in the Americas); notches at it and its harmonics. */
  hum_notch_hz?: 50 | 60 | null | false;
  /** Harmonics notched (default 3 → 50, 100, 150 Hz). */
  hum_harmonics?: number;
  denoise_audio?: { provider?: AudioEnhancerProvider; strength?: number } | null | false;
  declick?: Record<string, never> | null | false;
  /** Per-mic levelling: pass 1 = `measured_lufs` (ingest analysis), pass 2 = static gain to `target_lufs`. */
  loudness?: { target_lufs: number; measured_lufs?: number } | null | false;

  /** Tier B/C intermediate written by the enhance stage; render reads it instead of the original. */
  enhanced?: { uri?: string; path?: string; provider: CorrectionProvider; video?: boolean; audio?: boolean } | null | false;
}

/** Keys a person can switch on and off in Fix & enhance. */
export const CORRECTION_KEYS = [
  'deinterlace', 'colour_lut', 'white_balance', 'exposure', 'denoise_video', 'stabilize', 'fps', 'upscale',
  'highpass_hz', 'hum_notch_hz', 'denoise_audio', 'declick', 'loudness',
] as const;
export type CorrectionKey = (typeof CORRECTION_KEYS)[number];
export const VIDEO_CORRECTION_KEYS: readonly CorrectionKey[] = ['deinterlace', 'colour_lut', 'white_balance', 'exposure', 'denoise_video', 'stabilize', 'fps', 'upscale'];
export const AUDIO_CORRECTION_KEYS: readonly CorrectionKey[] = ['highpass_hz', 'hum_notch_hz', 'denoise_audio', 'declick', 'loudness'];

/** A correction is on when its key is present and not null/false. */
export function correctionOn(c: Corrections | null | undefined, key: CorrectionKey): boolean {
  const v = c?.[key];
  return v !== undefined && v !== null && v !== false;
}

/** `cf-media analyze` output, stored as `sources.media_info.analysis`. */
export interface MediaAnalysis {
  analyzer?: string;
  duration_ms?: number;
  sampled_ms?: number;
  video?: {
    width: number; height: number; fps: number; vfr: boolean;
    frames_analyzed: number;
  } | null;
  interlaced: boolean | null;
  interlace?: { tff: number; bff: number; progressive: number; undetermined: number } | null;
  /** Field order when interlaced. */
  parity?: 'tff' | 'bff' | null;
  /** 0..1 (sigma of the luma noise / 15 on the 8-bit scale). */
  noise_level: number | null;
  noise_sigma?: number | null;
  /** 0..1 */
  mean_luma: number | null;
  /** % of pixels crushed (≤ 2) or blown (≥ 253). */
  clipped_pct: number | null;
  clipped_low_pct?: number | null;
  clipped_high_pct?: number | null;
  /** 0..1 high-frequency camera motion (jitter), 0 for a locked-off camera. */
  shake_score: number | null;
  shake_px?: number | null;
  colour: { mean_rgb: [number, number, number]; p5: [number, number, number]; p95: [number, number, number]; cast?: number } | null;
  audio: {
    noise_floor_db: number; hum_hz: 50 | 60 | null; hum_db?: number; clipping_pct: number; lufs: number | null;
    peak_db?: number;
  } | null;
}

export const correctionsSchema = {
  $id: 'https://castfeeds.com/schema/corrections-1.json',
  title: 'Castfeeds correction profile v1',
  type: 'object',
  additionalProperties: false,
  $defs: {
    off: { enum: [null, false] },
    vec3: { type: 'array', items: { type: 'number' }, minItems: 3, maxItems: 3 },
  },
  properties: {
    schema: { const: 'castfeeds.corrections/1' },
    origin: { enum: ['auto', 'human'] },
    updated_at: { type: 'string' },
    deinterlace: {
      oneOf: [
        { $ref: '#/$defs/off' },
        { type: 'object', additionalProperties: false, properties: { parity: { enum: ['auto', 'tff', 'bff'] } } },
      ],
    },
    colour_lut: {
      oneOf: [
        { $ref: '#/$defs/off' },
        {
          type: 'object',
          additionalProperties: false,
          properties: {
            uri: { type: 'string', minLength: 1, maxLength: 1024 },
            path: { type: 'string', minLength: 1, maxLength: 4096 },
            reference_id: { type: 'string', maxLength: 80 },
            strength: { type: 'number', minimum: 0, maximum: 1 },
            delta_e_before: { type: 'number', minimum: 0 },
            delta_e_after: { type: 'number', minimum: 0 },
            transfer: {
              type: 'object',
              required: ['space', 'src_mean', 'src_std', 'ref_mean', 'ref_std'],
              additionalProperties: false,
              properties: {
                space: { const: 'oklab' },
                src_mean: { $ref: '#/$defs/vec3' },
                src_std: { $ref: '#/$defs/vec3' },
                ref_mean: { $ref: '#/$defs/vec3' },
                ref_std: { $ref: '#/$defs/vec3' },
                lut_size: { type: 'integer', minimum: 2, maximum: 65 },
              },
            },
          },
        },
      ],
    },
    white_balance: {
      oneOf: [
        { $ref: '#/$defs/off' },
        { type: 'object', additionalProperties: false, properties: { method: { enum: ['grayworld', 'colorcorrect'] } } },
      ],
    },
    exposure: {
      oneOf: [
        { $ref: '#/$defs/off' },
        {
          type: 'object',
          additionalProperties: false,
          properties: {
            gamma: { type: 'number', minimum: 0.3, maximum: 3 },
            brightness: { type: 'number', minimum: -0.5, maximum: 0.5 },
            contrast: { type: 'number', minimum: 0.3, maximum: 2.5 },
            saturation: { type: 'number', minimum: 0, maximum: 3 },
          },
        },
      ],
    },
    denoise_video: {
      oneOf: [
        { $ref: '#/$defs/off' },
        {
          type: 'object',
          additionalProperties: false,
          properties: { strength: { type: 'number', minimum: 0, maximum: 1 }, provider: { enum: ['ffmpeg', 'realesrgan', 'topaz'] } },
        },
      ],
    },
    stabilize: {
      oneOf: [
        { $ref: '#/$defs/off' },
        {
          type: 'object',
          additionalProperties: false,
          properties: { smoothing: { type: 'integer', minimum: 1, maximum: 120 }, shakiness: { type: 'integer', minimum: 1, maximum: 10 } },
        },
      ],
    },
    fps: {
      oneOf: [
        { $ref: '#/$defs/off' },
        {
          type: 'object',
          required: ['fps'],
          additionalProperties: false,
          properties: { fps: { type: 'number', minimum: 1, maximum: 120 }, mode: { enum: ['drop', 'blend'] } },
        },
      ],
    },
    upscale: {
      oneOf: [
        { $ref: '#/$defs/off' },
        { type: 'object', required: ['provider'], additionalProperties: false, properties: { provider: { enum: [...VIDEO_SCALERS] } } },
      ],
    },
    highpass_hz: { oneOf: [{ $ref: '#/$defs/off' }, { type: 'number', minimum: 20, maximum: 300 }] },
    hum_notch_hz: { oneOf: [{ $ref: '#/$defs/off' }, { enum: [50, 60] }] },
    hum_harmonics: { type: 'integer', minimum: 1, maximum: 8 },
    denoise_audio: {
      oneOf: [
        { $ref: '#/$defs/off' },
        {
          type: 'object',
          additionalProperties: false,
          properties: { provider: { enum: [...AUDIO_ENHANCERS] }, strength: { type: 'number', minimum: 0, maximum: 1 } },
        },
      ],
    },
    declick: { oneOf: [{ $ref: '#/$defs/off' }, { type: 'object', additionalProperties: false, properties: {} }] },
    loudness: {
      oneOf: [
        { $ref: '#/$defs/off' },
        {
          type: 'object',
          required: ['target_lufs'],
          additionalProperties: false,
          properties: { target_lufs: { type: 'number', minimum: -40, maximum: -5 }, measured_lufs: { type: 'number', minimum: -120, maximum: 10 } },
        },
      ],
    },
    enhanced: {
      oneOf: [
        { $ref: '#/$defs/off' },
        {
          type: 'object',
          required: ['provider'],
          additionalProperties: false,
          properties: {
            uri: { type: 'string', minLength: 1, maxLength: 1024 },
            path: { type: 'string', minLength: 1, maxLength: 4096 },
            provider: { enum: [...CORRECTION_PROVIDERS] },
            video: { type: 'boolean' },
            audio: { type: 'boolean' },
          },
        },
      ],
    },
  },
} as const;

/** Parameters used when a person switches a correction on that the worker did not recommend. */
export const DEFAULT_CORRECTION_PARAMS: { [K in CorrectionKey]-?: Exclude<NonNullable<Corrections[K]>, false> } = {
  deinterlace: { parity: 'auto' },
  colour_lut: { strength: 1 },
  white_balance: { method: 'grayworld' },
  exposure: { gamma: 1.2 },
  denoise_video: { strength: 0.4, provider: 'ffmpeg' },
  stabilize: { smoothing: 15, shakiness: 5 },
  fps: { fps: 30, mode: 'drop' },
  upscale: { provider: 'lanczos' },
  highpass_hz: 80,
  hum_notch_hz: 50,
  denoise_audio: { provider: 'ffmpeg', strength: 0.5 },
  declick: {},
  loudness: { target_lufs: -20 },
};

/**
 * Data fields that only the worker writes (LUT location, statistics, measurements). Overrides from people
 * never set them; they always come from the recommendation.
 */
export const CORRECTION_DATA_FIELDS = ['uri', 'path', 'transfer', 'reference_id', 'delta_e_before', 'delta_e_after', 'measured_lufs'] as const;

/** A person's change to one key: `true` = on (recommended or default parameters), `null`/`false` = off, object = on with these parameters. */
export type CorrectionOverride = boolean | null | number | Record<string, unknown>;
export type CorrectionOverrides = Partial<Record<CorrectionKey, CorrectionOverride>> & { hum_harmonics?: number };

function stripData(v: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, x] of Object.entries(v)) if (!(CORRECTION_DATA_FIELDS as readonly string[]).includes(k)) out[k] = x;
  return out;
}

export interface EffectiveCorrections {
  corrections: Corrections;
  /** Keys a person switched on that cannot run yet (e.g. a camera match before sync made the LUT). */
  warnings: string[];
}

/**
 * The profile render uses: the worker's recommendation, with a person's overrides on top.
 * Overrides are stored per key in `sources.corrections`, so a later recommendation (for example the LUT
 * written at sync) still reaches every key the person did not touch.
 */
export function effectiveCorrections(
  recommended: Corrections | null | undefined,
  overrides: CorrectionOverrides | null | undefined,
  enhanced?: Corrections['enhanced'],
): EffectiveCorrections {
  const rec: Corrections = { ...(recommended ?? {}) };
  const out: Record<string, unknown> = {};
  const warnings: string[] = [];
  for (const key of CORRECTION_KEYS) {
    const r = rec[key];
    const recOn = r !== undefined && r !== null && r !== false;
    const has = overrides != null && Object.prototype.hasOwnProperty.call(overrides, key);
    if (!has) {
      if (recOn) out[key] = r;
      continue;
    }
    const o = overrides![key];
    if (o === null || o === false || o === undefined) continue;
    const base = recOn ? r : DEFAULT_CORRECTION_PARAMS[key];
    if (typeof base === 'number') {
      out[key] = typeof o === 'number' ? o : base;
    } else if (o === true) {
      out[key] = base;
    } else if (typeof o === 'object') {
      out[key] = { ...(base as Record<string, unknown>), ...stripData(o as Record<string, unknown>) };
    } else {
      out[key] = base;
    }
    if (key === 'colour_lut') {
      const lut = out[key] as { uri?: string; path?: string; transfer?: ColourTransfer };
      if (!lut.uri && !lut.path && !lut.transfer) {
        delete out[key];
        warnings.push('colour_lut: no camera-match LUT exists for this source yet (it is made at sync, against the reference camera)');
      }
    }
  }
  const harmonics = overrides?.hum_harmonics ?? rec.hum_harmonics;
  if (out.hum_notch_hz !== undefined && harmonics !== undefined) out.hum_harmonics = harmonics;
  if (enhanced) out.enhanced = enhanced;
  else if (rec.enhanced) out.enhanced = rec.enhanced;
  const anyOverride = overrides != null && Object.keys(overrides).length > 0;
  return { corrections: { schema: 'castfeeds.corrections/1', origin: anyOverride ? 'human' : 'auto', ...(out as Corrections) }, warnings };
}

/**
 * Canonical text of a profile for hashing (amendment 9b: `enhanced/<source_id>/<profile_hash>.<ext>`): the
 * corrections that change pixels or samples, with sorted keys and no whitespace. Bookkeeping (`schema`, `origin`,
 * `updated_at`) and the `enhanced` intermediate itself are left out, as are LUT locations (`uri`, `path`), so the
 * same profile always gives the same text. `extra` adds job parameters (e.g. a preview's `at_ms`).
 */
export function canonicalProfile(c: Corrections | null | undefined, extra: Record<string, unknown> = {}): string {
  const TOP_LEVEL_DROP = ['schema', 'origin', 'updated_at', 'enhanced'];
  const NESTED_DROP = ['uri', 'path'];
  const norm = (v: unknown, drop: string[]): unknown => {
    if (Array.isArray(v)) return v.map((x) => norm(x, NESTED_DROP));
    if (!v || typeof v !== 'object') return v;
    const o = v as Record<string, unknown>;
    const keys = Object.keys(o).filter((k) => !drop.includes(k) && o[k] !== undefined && o[k] !== null && o[k] !== false);
    return Object.fromEntries(keys.sort().map((k) => [k, norm(o[k], NESTED_DROP)]));
  };
  return JSON.stringify({ corrections: norm(c ?? {}, TOP_LEVEL_DROP), ...(norm(extra, []) as Record<string, unknown>) });
}
